/**
 * The real offline mutation queue - Phase 1 of the offline-first roadmap.
 * Built on offlineDb.ts's IndexedDB `pendingActions` store, so a queued
 * write survives a refresh (unlike the old in-memory-only `pendingSync`
 * counter in store.ts, which this replaces).
 *
 * Design, per the product decisions this was scoped against:
 *  - "Server wins, flag for manual review": a failed replay (validation
 *    rejection OR a transient network error - not distinguished) is never
 *    auto-retried and never silently dropped. It sits as `status: "failed"`
 *    with the server's own message until the user explicitly retries or
 *    discards it.
 *  - Client-driven sync, not solely the Background Sync API (no Safari/iOS
 *    support, which matters directly for the low-connectivity-Africa
 *    audience this is for) - flushQueue() is called on the browser's
 *    `online` event, on tab foreground, and via a manual "Sync now".
 *  - Sequential replay, not parallel - safer for financial records than
 *    firing every queued write at once.
 */

import { AxiosError } from "axios";
import { useQuery } from "@tanstack/react-query";
import { api } from "./api";
import { getOfflineDb, type PendingAction } from "./offlineDb";
import { queryClient } from "./queryClient";
import { useStore } from "./store";
import { readConnectionType } from "./useConnectionType";

const QUEUE_KEY = ["offline-queue"];

// One replayer per queued action `type`. raiseSpend/submitEvidence's payload
// already matches their api.* call shape 1:1, so they plug in directly; the
// site-visit ones take multiple positional args, so each gets a thin
// wrapper unpacking the payload object enqueueAction was called with.
const REPLAYERS: Record<string, (payload: any) => Promise<unknown>> = {
  raiseSpend: api.raiseSpend,
  submitEvidence: api.submitEvidence,
  siteVisitBegin: (p: { visitId: string }) => api.beginSiteVisit(p.visitId),
  siteVisitJudge: (p: { visitId: string; itemId: string; judgement: string }) =>
    api.setChecklistJudgement(p.visitId, p.itemId, p.judgement),
  siteVisitCapture: (p: { visitId: string; input: Parameters<typeof api.addVisitCapture>[1] }) =>
    api.addVisitCapture(p.visitId, p.input),
  siteVisitComplete: (p: { visitId: string; summary?: string }) => api.completeSiteVisit(p.visitId, p.summary),
};

// Query keys to refresh once a queued action of this type actually lands on
// the server - e.g. a synced raiseSpend needs the reporter Inbox
// (client/src/screens/reporter/Inbox.tsx) to pick up the real row promptly,
// not just wait on the reconnect-triggered refetch it also gets for free
// (which can race the sync itself).
const POST_SYNC_INVALIDATIONS: Record<string, string[]> = {
  raiseSpend: ["reporter-inbox"],
  siteVisitBegin: ["site-visits"],
  siteVisitJudge: ["site-visits"],
  siteVisitCapture: ["site-visits"],
  siteVisitComplete: ["site-visits"],
};

function extractErrorMessage(e: unknown): string {
  if (e instanceof AxiosError) {
    const data = e.response?.data as { error?: string; message?: string } | undefined;
    return data?.message || data?.error || e.message;
  }
  return e instanceof Error ? e.message : "Something went wrong";
}

function invalidateQueue() {
  queryClient.invalidateQueries({ queryKey: QUEUE_KEY });
}

export async function enqueueAction(type: string, payload: unknown): Promise<string> {
  const db = await getOfflineDb();
  const id = crypto.randomUUID();
  await db.put("pendingActions", {
    id,
    type,
    payload,
    createdAt: Date.now(),
    status: "pending",
    lastError: null,
  });
  invalidateQueue();
  return id;
}

/** Replays every `pending` row in turn. Never touches `failed` rows - those
 * only move again via an explicit retryAction() call. */
export async function flushQueue(): Promise<void> {
  const db = await getOfflineDb();
  const pending = await db.getAllFromIndex("pendingActions", "by-status", "pending");

  for (const action of pending) {
    await db.put("pendingActions", { ...action, status: "syncing" });
    invalidateQueue();

    const replay = REPLAYERS[action.type];
    try {
      if (!replay) throw new Error(`No replayer registered for queued action type "${action.type}"`);
      await replay(action.payload);
      await db.delete("pendingActions", action.id);
      const keys = POST_SYNC_INVALIDATIONS[action.type];
      if (keys) queryClient.invalidateQueries({ queryKey: keys });
    } catch (e) {
      await db.put("pendingActions", { ...action, status: "failed", lastError: extractErrorMessage(e) });
    }
  }
  invalidateQueue();
}

/**
 * Resets any row still marked `"syncing"` back to `"pending"`. flushQueue()
 * marks a row `"syncing"` *before* awaiting its replay (see above); if the
 * tab is closed/refreshed in that window, the row is never confirmed to
 * have reached the server, but also never revisited - `flushQueue()` only
 * ever selects `"pending"` rows. A `"syncing"` row found at boot can only be
 * one abandoned by a prior page load, so it's safe to treat as not-yet-sent
 * and hand back to the normal pending queue. Called once before the first
 * flush of a session (initOfflineSync below) - never mid-session, since a
 * row legitimately mid-flight during THIS session's own flushQueue() call
 * must not be reset out from under it.
 */
export async function reconcileAbandonedSyncing(): Promise<void> {
  const db = await getOfflineDb();
  const stuck = await db.getAllFromIndex("pendingActions", "by-status", "syncing");
  for (const action of stuck) {
    await db.put("pendingActions", { ...action, status: "pending" });
  }
  if (stuck.length > 0) invalidateQueue();
}

/** Explicit user-triggered retry for one failed action - moves it back to
 * `pending` and immediately attempts the whole queue (picks up any other
 * pending rows too, not just this one). Never automatic. */
export async function retryAction(id: string): Promise<void> {
  const db = await getOfflineDb();
  const action = await db.get("pendingActions", id);
  if (!action) return;
  await db.put("pendingActions", { ...action, status: "pending", lastError: null });
  invalidateQueue();
  await flushQueue();
}

export async function discardAction(id: string): Promise<void> {
  const db = await getOfflineDb();
  await db.delete("pendingActions", id);
  invalidateQueue();
}

export interface OfflineQueueState {
  pending: PendingAction[];
  syncing: PendingAction[];
  failed: PendingAction[];
}

export function useOfflineQueue(): OfflineQueueState {
  const { data } = useQuery({
    queryKey: QUEUE_KEY,
    queryFn: async () => {
      const db = await getOfflineDb();
      return db.getAll("pendingActions");
    },
    staleTime: Infinity, // only ever refetched via invalidateQueue() above
    // This queryFn never touches the network at all (pure IndexedDB read),
    // but without this, TanStack Query's default networkMode: "online"
    // still pauses it - and any refetch invalidateQueue() triggers - while
    // navigator.onLine is false. enqueueAction() (called right after a
    // reporter submits offline) writes to IndexedDB fine and calls
    // invalidateQueue(), but that refetch sat paused, so the Inbox kept
    // showing its pre-submission (stale) cached data until reconnecting
    // finally let the paused refetch run - the newly-raised ask was
    // genuinely queued the whole time, just invisible until then.
    networkMode: "always",
  });

  const all = data ?? [];
  return {
    pending: all.filter((a) => a.status === "pending"),
    syncing: all.filter((a) => a.status === "syncing"),
    failed: all.filter((a) => a.status === "failed"),
  };
}

/**
 * Whether an *automatic* sync trigger (below) should actually flush right
 * now. Only the "Sync only on Wi-Fi" reporter setting (store.ts) can say no,
 * and only when the connection is confidently detected as cellular - a
 * connection the browser can't classify ("unknown", the common Safari/iOS
 * case per useConnectionType.ts's own doc comments) still syncs, since
 * treating "unknown" as "block" would silently strand those users' queues
 * forever. Never consulted by flushQueue()/retryAction() themselves, so a
 * manual "Sync now" always works regardless of this setting.
 */
export function shouldAutoSync(): boolean {
  if (!useStore.getState().syncOnlyOnWifi) return true;
  return readConnectionType() !== "cellular";
}

/** Called once at app boot (main.tsx) - wires the real triggers for a
 * background flush. Returns a cleanup function (unused today since main.tsx
 * never unmounts, but keeps this consistent with how every other
 * event-listener hook in this codebase cleans up after itself). */
export function initOfflineSync(): () => void {
  const onOnline = () => { if (shouldAutoSync()) void flushQueue(); };
  const onVisibility = () => {
    if (document.visibilityState === "visible" && shouldAutoSync()) void flushQueue();
  };
  window.addEventListener("online", onOnline);
  document.addEventListener("visibilitychange", onVisibility);
  // Also attempt a flush at boot, in case there's a queue left over from a
  // previous session that never got the chance to sync - first reconciling
  // any row abandoned mid-sync by that previous session (see
  // reconcileAbandonedSyncing above), so it's actually eligible to flush.
  void reconcileAbandonedSyncing().then(() => { if (shouldAutoSync()) void flushQueue(); });
  return () => {
    window.removeEventListener("online", onOnline);
    document.removeEventListener("visibilitychange", onVisibility);
  };
}
